// Pure structural helpers for inheritance-pattern detection, all operating on
// a TokenisedPedigree + a single diseaseId (per-disease — see plan §12.3).

export const isAffectedBy = (ind, diseaseId) =>
  !!ind && ind.affectedDiseases.has(diseaseId);

export const affectedList = (ped, diseaseId) =>
  ped.individuals.filter((i) => isAffectedBy(i, diseaseId));

export const parentsOf = (ped, ind) =>
  [ind.fatherId, ind.motherId]
    .map((id) => id && ped.byPid.get(id))
    .filter(Boolean);

export const childrenOf = (ped, ind) =>
  ind.childrenIds.map((id) => ped.byPid.get(id)).filter(Boolean);

export const affectedMales = (ped, diseaseId) =>
  affectedList(ped, diseaseId).filter((i) => i.sex === 'M');

export const affectedFemales = (ped, diseaseId) =>
  affectedList(ped, diseaseId).filter((i) => i.sex === 'F');

export const hasConsanguinity = (ped) =>
  (ped.consanguinityLoops || []).length > 0;

// Count (affected parent → affected child) pairs = vertical transmission.
export function verticalPairs(ped, diseaseId) {
  let count = 0;
  for (const child of affectedList(ped, diseaseId))
    for (const parent of parentsOf(ped, child))
      if (isAffectedBy(parent, diseaseId)) count++;
  return count;
}

// An affected male whose father is also affected (excludes X-linked / MT).
export function hasMaleToMaleTransmission(ped, diseaseId) {
  for (const child of affectedList(ped, diseaseId)) {
    if (child.sex !== 'M') continue;
    const father = child.fatherId && ped.byPid.get(child.fatherId);
    if (father && isAffectedBy(father, diseaseId)) return true;
  }
  return false;
}

// An affected father with an affected child via him (excludes MT).
export function hasPaternalTransmission(ped, diseaseId) {
  for (const ind of affectedMales(ped, diseaseId))
    for (const child of childrenOf(ped, ind))
      if (isAffectedBy(child, diseaseId)) return true;
  return false;
}

export function hasMaternalTransmission(ped, diseaseId) {
  for (const ind of affectedFemales(ped, diseaseId))
    for (const child of childrenOf(ped, ind))
      if (isAffectedBy(child, diseaseId)) return true;
  return false;
}

export function bothSexesAffected(ped, diseaseId) {
  const a = affectedList(ped, diseaseId);
  return a.some((i) => i.sex === 'M') && a.some((i) => i.sex === 'F');
}

// Affected individuals whose known parents are all unaffected (≥1 parent known).
export function affectedWithUnaffectedParents(ped, diseaseId) {
  return affectedList(ped, diseaseId).filter((child) => {
    const parents = parentsOf(ped, child);
    return (
      parents.length > 0 && parents.every((p) => !isAffectedBy(p, diseaseId))
    );
  });
}

// mtDNA genes use the HGNC "MT-" prefix; everything else is nuclear. A
// pathogenic nuclear variant is incompatible with mitochondrial inheritance and
// points to a chromosomal (usually autosomal) basis.
const isMitochondrialGene = (gene) => /^MT-/i.test(String(gene || '').trim());

// Nuclear genes with a pathogenic variant carried by someone affected by the
// disease — molecular evidence of a nuclear genetic cause. Heuristic: assumes a
// recorded pathogenic variant in an affected person explains their disease.
export function affectedPathogenicNuclearGenes(ped, diseaseId) {
  const genes = new Set();
  for (const ind of affectedList(ped, diseaseId))
    for (const g of ind.genotypes || [])
      if (
        g.interpretation === 'pathogenic' &&
        g.gene &&
        !isMitochondrialGene(g.gene)
      )
        genes.add(g.gene);
  return [...genes];
}

// Number of sibships with ≥2 affected sharing the same unaffected parents.
export function affectedSiblingClustersWithUnaffectedParents(ped, diseaseId) {
  const byParentKey = new Map();
  for (const ind of affectedWithUnaffectedParents(ped, diseaseId)) {
    const key = [ind.fatherId, ind.motherId].sort().join('|');
    byParentKey.set(key, (byParentKey.get(key) || 0) + 1);
  }
  return [...byParentKey.values()].filter((n) => n >= 2).length;
}
